/**
 * Login Page
 * Allows users to authenticate with email, password, and tenant slug
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import cinrmLogo from '../assets/cinrmlogo.png';
import { Toast } from '../components/Toast';
import { homePathFor } from '../utils/permissions';
import { MfaChallengeRequiredError } from '../api/mfaTypes';
import { getErrorCode, getErrorMessage } from '../utils/apiError';

/**
 * Second sign-in step when the user has two-step verification on (tracker
 * item 63): a 6-digit code from the authenticator app, or a recovery code.
 */
function MfaCodeStep({ onDone, onExpired }: { onDone: (path: string) => void; onExpired: (message: string) => void }) {
  const { completeMfaLogin, cancelMfaLogin, isLoading } = useAuth();
  const [useRecovery, setUseRecovery] = useState(false);
  const [value, setValue] = useState('');
  const [stepError, setStepError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStepError(null);
    const entered = value.trim();
    if (!entered) {
      setStepError(useRecovery ? 'Enter one of your recovery codes' : 'Enter the 6-digit code from your app');
      return;
    }
    try {
      const signedIn = await completeMfaLogin(useRecovery ? { recoveryCode: entered } : { code: entered });
      onDone(homePathFor(signedIn));
    } catch (err) {
      if (getErrorCode(err) === 'MFA_CHALLENGE_INVALID') {
        cancelMfaLogin();
        onExpired('Your sign-in expired. Enter your password again.');
        return;
      }
      setStepError(getErrorMessage(err, 'That code did not work'));
      setValue('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate aria-label="Two-step verification">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Two-step verification</h2>
        <p className="mt-1 text-sm text-gray-600">
          {useRecovery
            ? 'Enter one of the recovery codes you saved when you set up two-step verification. Each code works once.'
            : 'Open your authenticator app and enter the 6-digit code for this account.'}
        </p>
      </div>
      {stepError && <Toast kind="error">{stepError}</Toast>}
      <div>
        <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700 mb-1">
          {useRecovery ? 'Recovery code' : 'Authentication code'}
        </label>
        <input
          id="mfa-code"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={isLoading}
          inputMode={useRecovery ? 'text' : 'numeric'}
          autoComplete={useRecovery ? 'off' : 'one-time-code'}
          autoCapitalize="characters"
          maxLength={useRecovery ? 13 : 7}
          placeholder={useRecovery ? 'XXXXX-XXXXX' : '123456'}
          autoFocus
          className="w-full px-4 py-2.5 sm:py-2 text-base tracking-widest border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
        />
      </div>
      <button
        type="submit"
        disabled={isLoading}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 sm:py-2 px-4 rounded-md transition duration-200"
      >
        {isLoading ? 'Checking...' : 'Verify'}
      </button>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <button
          type="button"
          onClick={() => {
            setUseRecovery((v) => !v);
            setValue('');
            setStepError(null);
          }}
          className="inline-flex min-h-[44px] items-center text-blue-600 hover:text-blue-800"
        >
          {useRecovery ? 'Use the authenticator app instead' : 'Use a recovery code'}
        </button>
        <button type="button" onClick={cancelMfaLogin} className="inline-flex min-h-[44px] items-center text-gray-600 hover:text-gray-800">
          Back to sign in
        </button>
      </div>
    </form>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError, mfaChallenge } = useAuth();

  // Form state
  const [tenantSlug, setTenantSlug] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    clearError();

    try {
      const signedIn = await login(tenantSlug, email, password);
      // A TECHNICIAN lands on /tech (item 42), a CUSTOMER_PORTAL user on /portal (item 45)
      navigate(homePathFor(signedIn), { replace: true });
    } catch (err) {
      // MFA on: the code step takes over (mfaChallenge is set)
      if (err instanceof MfaChallengeRequiredError) {
        setPassword('');
        return;
      }
      // The server's message (e.g. 429 MFA_LOCKED), not axios's generic text
      setFormError(getErrorMessage(err, 'Login failed'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-6 sm:p-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="mb-2">
            <img src={cinrmLogo} alt="CinRM" className="w-64 max-w-full h-auto mx-auto" />
          </h1>
          <p className="text-gray-600">Service-based business management</p>
        </div>

        {mfaChallenge ? (
          <MfaCodeStep onDone={(path) => navigate(path, { replace: true })} onExpired={setFormError} />
        ) : (
        <>
        {(formError || error) && <Toast kind="error">{formError || error}</Toast>}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tenant Slug */}
          <div>
            <label htmlFor="tenant" className="block text-sm font-medium text-gray-700 mb-1">
              Tenant/Organization
            </label>
            <input
              id="tenant"
              type="text"
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              disabled={isLoading}
              placeholder="your-organization"
              required
              className="w-full px-4 py-2.5 sm:py-2 text-base border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              placeholder="user@example.com"
              required
              className="w-full px-4 py-2.5 sm:py-2 text-base border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              placeholder="••••••••"
              required
              className="w-full px-4 py-2.5 sm:py-2 text-base border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 sm:py-2 px-4 rounded-md transition duration-200 mt-6"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        </>
        )}

        <div className="text-center mt-6">
          <Link to="/portal/login" className="inline-block py-3 px-2 sm:p-0 text-sm text-blue-600 hover:text-blue-800">
            Customer? Sign in to your online account
          </Link>
          <br />
          <Link to="/admin/login" className="inline-block py-3 px-2 sm:p-0 text-sm sm:text-xs text-gray-400 hover:text-gray-600">
            Staff login
          </Link>
        </div>
      </div>
    </div>
  );
}
